import type { InstagramClient } from '../api/impl/InstagramClient';
import type { Message as LineMessage } from '../api/impl/LineMessageClient';
import { type PostMessageToChatbotFn, type PostMessageToChatbotResult } from '../api/impl/PlatformChatbotAPI';
import type { DelayedAction, ReplicantPlatformMessagePayloadEncoderAPI } from '../api/ReplicantAPI';
import {
    AnalyticsEvent,
    AnalyticsPayload,
    CHATBOT_MESSAGE_FAILURE,
    CHATBOT_MESSAGE_SUCCESS,
    CHATBOT_RESPONSE_FAILURE,
    CHATBOT_RESPONSE_SUCCESS,
    generateInsertId,
    WEBPUSH_NOTIFICATION_FAILURE,
    WEBPUSH_NOTIFICATION_SUCCESS,
} from '../common/Analytics';
import type { ChatbotMetainfo, default as DB } from '../db/DB';
import { ReplicantError } from '../Errors';
import logger from '../logger';
import type { ChatbotMessageInstance, DeliveryChannel, RendererResult, RendererType } from '../ReplicantChatbot';
import type { Replicant } from '../ReplicantConfig';
import type { ReplicantPlatform } from '../ReplicantPlatform';
import { isAndroidDeviceTokenExpired, isAppleDeviceTokenExpired } from '../utils/DeviceTokenUtils';
import { ResponseError } from '../utils/ResponseError';
import type { TwilioClient } from '../utils/Twilio';
import { duration, generateRandomId, unreachable } from '../utils/Utils';
import type { WebPushClient } from '../utils/WebPush';
import { DiscordClient } from './Discord';
import type { FBGamingGraphAPIClient } from './FBGamingGraphAPI';
import type { APIAccessInterface } from './ServerReplicant';
import type { SendAndroidPushNotificationFn } from './ServerReplicantAndroidPushNotifications';
import type { SendIOSPushNotificationFn } from './ServerReplicantIOSPushNotifications';
import type { TelegramClient, TelegramMessage } from './Telegram';

export type SendChatbotMessageOptions = {
    receiverId: string;
    senderId: string;
    message: ChatbotMessageInstance;

    /** Specific delivery channel to use instead of the default fallback strategy */
    forceChannel?: DeliveryChannel;
};

export type SendChatbotMessageFn = (opts: SendChatbotMessageOptions) => Promise<boolean>;

export type SendLineChatbotMessageOptions =
    | {
          type: 'push';
          receiverId: string;
          senderId: string;
          message: ChatbotMessageInstance;
          sendMessageToLine: (message: LineMessage) => Promise<PostMessageToChatbotResult>;
          scheduledActionId?: string;
      }
    | {
          type: 'reply';
          receiverId: string;
          senderId: string;
          replyToken: string;
          message: ChatbotMessageInstance;
          sendMessageToLine: (message: LineMessage) => Promise<PostMessageToChatbotResult>;
          scheduledActionId?: string;
      };

export type SendTelegramChatbotMessageFn = (opts: {
    analyticsUserProperties?: Record<string, unknown>;

    extraEventProperties?: Record<string, unknown>;

    /** Telegram chat ID. */
    chatId: number | string;

    payload: AnalyticsPayload;

    /** Receiver's Replicant user ID. */
    receiverId: string;

    templateName: string;

    timeoutMs?: number;

    message: TelegramMessage;

    /** Telegram message ID. Specify to send a reply message. */
    replyToMessageId?: number;

    /** Whether the message should be pinned, defaults to false. Any previously pinned message will be unpinned. */
    pinned?: boolean;
}) => Promise<{ ok: true } | { ok: false; error: unknown }>;

type ChannelsStatus = { [channel in DeliveryChannel]?: string };

export type SendChatbotMessageResult =
    | { ok: true; channels: ChannelsStatus }
    | { ok: false; channels: ChannelsStatus; reason: string };

export type ServerReplicantChatbotOptions<T extends Replicant> = {
    apiAccess: APIAccessInterface<T>;
    createPayloadEncoderAPI: (opts: {
        userId: string;
        sessionId: string;
        receiverId: string;
        delayedActions: DelayedAction[];
    }) => ReplicantPlatformMessagePayloadEncoderAPI;
    db: DB<T['state'], T['messages']>;
    fbGamingGraphAPIClient?: FBGamingGraphAPIClient;
    instagramClient?: InstagramClient;
    telegramClient?: TelegramClient;
    discordClient?: DiscordClient;
    postFBMessage: PostMessageToChatbotFn;
    postLineMessage: PostMessageToChatbotFn;
    postZoomMessage: PostMessageToChatbotFn;
    sendAndroidPushNotification: SendAndroidPushNotificationFn;
    sendIOSPushNotification: SendIOSPushNotificationFn;
    webPushClient: WebPushClient;
    twilioClient: TwilioClient;
};

export const ON_GAME_END_NOTIFICATION_ID = 'onGameEnd';

export default class ServerReplicantChatbot<T extends Replicant> {
    private db: DB<T['state'], T['messages']>;

    constructor(private opts: ServerReplicantChatbotOptions<T>) {
        this.db = opts.db;
    }

    // TODO: Refactor this to be used by sendChatbotMessage and track analytics and everything
    async sendRawChatbotMessage(
        messengerId: string,
        payload: any,
    ): Promise<{ statusCode: number; body?: string | undefined }> {
        if (this.opts.apiAccess.platform !== 'fb') {
            throw Error('Only supported by the Facebook platform');
        }

        return this.opts.postFBMessage(messengerId, payload);
    }

    async sendChatbotMessage(opts: SendChatbotMessageOptions): Promise<SendChatbotMessageResult> {
        // Currently chatbot is supported only on certain platforms
        if (!['fb', 'line', 'zoom', 'web'].includes(this.opts.apiAccess.platform)) {
            throw new ReplicantError(
                'Chatbot messages are not supported on platform ' + this.opts.apiAccess.platform,
                'server_error',
                'invalid_platform',
                'fatal',
            );
        }

        const { forceChannel, receiverId, senderId, message } = opts;

        if (!receiverId) {
            throw new ReplicantError(
                'Receiver of chatbot message invalid: ' + receiverId,
                'server_error',
                'chatbot_missing_receiver',
                'fatal',
            );
        }

        if (!senderId) {
            throw new ReplicantError(
                'Sender of chatbot message invalid: ' + senderId,
                'server_error',
                'chatbot_missing_sender',
                'fatal',
            );
        }

        const chatbot = this.opts.apiAccess.config.chatbot;
        if (!chatbot) {
            throw new ReplicantError(
                'Chatbot not configured.',
                'server_error',
                'chatbot_missing_configuration',
                'fatal',
            );
        }

        const receiverChatbotMetainfo = await this.db.chatbot.fetchMetainfo(receiverId);
        if (!receiverChatbotMetainfo) {
            const reason =
                'Receiver has not subscribed to the chatbot. See https://docs.dev.gc-internal.net/replicant/index.html#chatbot-integration.';
            logger.info('Skipping message send:', reason);
            return { ok: false, reason, channels: {} };
        }

        if (!receiverChatbotMetainfo.messengerId) {
            switch (this.opts.apiAccess.platform) {
                case 'line':
                    receiverChatbotMetainfo.messengerId = receiverId;
                    break;
                case 'zoom':
                    receiverChatbotMetainfo.messengerId = receiverChatbotMetainfo.email;
                    break;
            }
        }

        let isValidAppleDeviceToken = !!receiverChatbotMetainfo.appleDeviceToken;
        if (receiverChatbotMetainfo.appleDeviceToken) {
            if (!receiverChatbotMetainfo.appleDeviceTokenUpdatedAt) {
                const now = this.opts.apiAccess.dateNow();
                logger.info(`Chatbot - Apple device token update time being set for: ${receiverId} to ${now}`);
                await this.db.chatbot.setAppleDeviceToken(receiverId, receiverChatbotMetainfo.appleDeviceToken, now);
            } else if (
                isAppleDeviceTokenExpired(
                    receiverChatbotMetainfo.appleDeviceTokenUpdatedAt,
                    this.opts.apiAccess.dateNow(),
                )
            ) {
                logger.info(`Chatbot - Apple device token is considered expired for: ${receiverId}`);
                isValidAppleDeviceToken = false;
            }
        }

        let isValidAndroidDeviceToken = !!receiverChatbotMetainfo.androidDeviceToken;
        if (receiverChatbotMetainfo.androidDeviceToken) {
            if (!receiverChatbotMetainfo.androidDeviceTokenUpdatedAt) {
                const now = this.opts.apiAccess.dateNow();
                logger.info(`Chatbot - Android device token update time being set for: ${receiverId} to ${now}`);
                await this.db.chatbot.setAndroidDeviceToken(
                    receiverId,
                    receiverChatbotMetainfo.androidDeviceToken,
                    now,
                );
            } else if (
                isAndroidDeviceTokenExpired(
                    receiverChatbotMetainfo.androidDeviceTokenUpdatedAt,
                    this.opts.apiAccess.dateNow(),
                )
            ) {
                logger.info(`Chatbot - Android device token is considered expired for: ${receiverId}`);
                isValidAndroidDeviceToken = false;
            }
        }

        const sessionId = receiverChatbotMetainfo?.lastSessionId || 'sessionid-' + generateRandomId();

        const payload = {
            timestamp: this.opts.apiAccess.dateNow(),
            ...message.payload,
        };

        const sendParams = {
            payload,
            message,
            receiverChatbotMetainfo,
            receiverId,
            senderId,
            sessionId,
        };

        const channels: ChannelsStatus = {};
        const nativeNotificationPromises: Promise<{ ok: true } | { ok: false; reason: string }>[] = [];

        // 1. Try to send an iOS Push Notification
        if (isValidAppleDeviceToken && (!forceChannel || forceChannel === 'ios' || forceChannel === 'nativepush')) {
            // enforce position of apn results with arr index
            nativeNotificationPromises[0] = this.sendActivePushNotification(sendParams);
        }

        // 2. Try to send Android Push Notification
        if (
            isValidAndroidDeviceToken &&
            (!forceChannel || forceChannel === 'android' || forceChannel === 'nativepush')
        ) {
            // enforce position of fcm results with arr index
            nativeNotificationPromises[1] = this.sendAndroidPushNotification(sendParams);
        }

        // 3. If either of ios or android succeeds, return 'ok'
        if (nativeNotificationPromises.length > 0) {
            const channelResults = await Promise.all(nativeNotificationPromises);

            if (channelResults[0]) {
                const apnStatus = channelResults[0];
                if (apnStatus.ok) {
                    channels.ios = 'ok';
                } else {
                    channels.ios = apnStatus.reason;
                }
            }

            if (channelResults[1]) {
                const fcmStatus = channelResults[1];
                if (fcmStatus.ok) {
                    channels.android = 'ok';
                } else {
                    channels.android = fcmStatus.reason;
                }
            }

            if (channels.ios === 'ok' || channels.android === 'ok') {
                return { ok: true, channels };
            }
        }

        // 4. Try Web Push if they have a subscription
        if (receiverChatbotMetainfo.webPushSubscription && (!forceChannel || forceChannel === 'webpush')) {
            const result = await this.sendWebPushNotification(sendParams);
            channels.webpush = result.ok ? 'ok' : result.reason;
            if (result.ok) {
                return { ok: true, channels };
            }
        }

        // 5. Try SMS if the user is subscribed to SMS notifications
        if (receiverChatbotMetainfo.smsSubscribed && (!forceChannel || forceChannel === 'sms')) {
            const result = await this.sendSmsNotification(sendParams);
            channels.sms = result.ok ? 'ok' : result.reason;
            if (result.ok) {
                return { ok: true, channels };
            }
        }

        // 6. Try to send the Facebook, Line or Zoom chatbot message
        if (receiverChatbotMetainfo.messengerId && (!forceChannel || forceChannel === 'chatbot')) {
            const chatbotStatus = await this.sendPlatformChatbotMessage(sendParams);
            if (chatbotStatus.ok) {
                channels.chatbot = 'ok';
                return { ok: true, channels };
            }
            channels.chatbot = chatbotStatus.reason;
        }

        const reason =
            Object.values(channels).length > 0
                ? 'All delivery channels failed'
                : 'Skipping message, no delivery channel available for receiver';

        logger.warn('Chatbot message send failed:', reason, channels, sendParams);

        return { ok: false, reason, channels };
    }

    async sendLineChatbotMessage(opts: SendLineChatbotMessageOptions): Promise<{ ok: boolean; reason?: string }> {
        // Currently chatbot message batching is supported only on certain platforms
        if (!['line'].includes(this.opts.apiAccess.platform)) {
            throw new ReplicantError(
                'LINE messages are not supported on platform ' + this.opts.apiAccess.platform,
                'server_error',
                'invalid_platform',
                'fatal',
            );
        }

        const { receiverId, senderId, message, type, scheduledActionId } = opts;

        if (!receiverId) {
            throw new ReplicantError(
                'Receiver of chatbot message invalid: ' + receiverId,
                'server_error',
                'chatbot_missing_receiver',
                'fatal',
            );
        }

        if (!senderId) {
            throw new ReplicantError(
                'Sender of chatbot message invalid: ' + senderId,
                'server_error',
                'chatbot_missing_sender',
                'fatal',
            );
        }

        const chatbot = this.opts.apiAccess.config.chatbot;
        if (!chatbot) {
            throw new ReplicantError(
                'Chatbot not configured.',
                'server_error',
                'chatbot_missing_configuration',
                'fatal',
            );
        }

        const receiverChatbotMetainfo = await this.db.chatbot.fetchMetainfo(receiverId);
        if (!receiverChatbotMetainfo) {
            const reason =
                'Receiver has not subscribed to the chatbot. See https://docs.dev.gc-internal.net/replicant/index.html#chatbot-integration.';
            logger.info('Skipping message send:', reason);
            return { ok: false, reason };
        }

        if (!receiverChatbotMetainfo.messengerId) {
            receiverChatbotMetainfo.messengerId = receiverId;
        }

        const sessionId = receiverChatbotMetainfo?.lastSessionId || 'sessionid-' + generateRandomId();

        const sendParams =
            type === 'push'
                ? {
                      type,
                      receiverChatbotMetainfo,
                      receiverId,
                      senderId,
                      message,
                      payload: { timestamp: this.opts.apiAccess.dateNow(), ...message.payload },
                      sessionId,
                      sendMessageToLine: opts.sendMessageToLine,
                      scheduledActionId,
                  }
                : type === 'reply'
                ? {
                      type,
                      replyToken: opts.replyToken,
                      receiverChatbotMetainfo,
                      receiverId,
                      senderId,
                      message,
                      payload: { timestamp: this.opts.apiAccess.dateNow(), ...message.payload },
                      sessionId,
                      sendMessageToLine: opts.sendMessageToLine,
                      scheduledActionId,
                  }
                : unreachable(type);

        if (receiverChatbotMetainfo.messengerId) {
            const chatbotStatus = await this.sendLinePlatformChatbotMessage(sendParams);
            if (chatbotStatus.ok) {
                return { ok: true };
            }
        }

        const reason = 'Skipping message, no delivery channel available for receiver';

        logger.warn('Chatbot message send failed:', reason, sendParams);

        return { ok: false, reason };
    }

    async sendInstagramChatbotMessage(opts: {
        commentId?: string;
        receiverId: string;
        message: ChatbotMessageInstance;
        senderId: string;
    }) {
        const { instagramClient } = this.opts;

        if (!instagramClient) {
            throw Error(
                'Instagram client not configured. Make sure to set the INSTAGRAM_PAGE_* environment variables.',
            );
        }

        const receiverChatbotMetainfo = await this.db.chatbot.fetchMetainfo(opts.receiverId);

        const receiver = opts.commentId
            ? { commentId: opts.commentId }
            : receiverChatbotMetainfo?.instagramMessengerId
            ? { userId: receiverChatbotMetainfo.instagramMessengerId }
            : undefined;

        if (!receiver) {
            logger.warn('Skipping message send: receiver instagramMessengerId is not set');
            return;
        }

        await this.sendMessage(
            async (renderedMessage) => {
                const response = await instagramClient.postMessages({
                    receiver,
                    body: renderedMessage,
                });

                if (!response.ok) {
                    throw Error(response.body);
                }
            },
            {
                message: opts.message,
                receiverId: opts.receiverId,
                rendererType: 'instagram',
                senderId: opts.senderId,
            },
        );
    }

    async sendMessengerChatbotMessage(opts: { receiverId: string; senderId: string; message: ChatbotMessageInstance }) {
        const { message, receiverId, senderId } = opts;

        const renderer = message.renderers.facebook;
        if (!renderer) {
            throw Error(`No facebook renderer in message template ${message.name}`);
        }

        const { messengerId } = (await this.db.chatbot.fetchMetainfo(receiverId)) ?? {};

        if (!messengerId) {
            logger.warn('Skipping message send: receiver messengerId is not set');
            return;
        }

        await this.sendMessage(
            async (renderedMessage) => {
                const response = await this.opts.postFBMessage(messengerId, renderedMessage);

                if (response.statusCode < 200 || response.statusCode >= 300) {
                    throw Error(response.body);
                }
            },
            {
                chatbotType: 'messenger',
                message,
                receiverId,
                rendererType: 'facebook',
                senderId,
            },
        );
    }

    sendTelegramChatbotMessage: SendTelegramChatbotMessageFn = async (opts) => {
        if (!this.opts.telegramClient) {
            throw Error(
                'Telegram client not configured. Make sure to set the TELEGRAM_BOT_TOKEN environment variable.',
            );
        }

        const {
            analyticsUserProperties,
            chatId,
            extraEventProperties,
            message,
            payload,
            pinned,
            receiverId,
            replyToMessageId,
            templateName,
            timeoutMs,
        } = opts;

        const commonEventProps = {
            platform: 'telegram' as const,
            template: templateName,
            ...extraEventProperties,
            ...payload,
        };

        try {
            const unpinAllChatMessages = pinned
                ? this.opts.telegramClient.unpinAllChatMessages({ chatId, timeoutMs })
                : undefined;

            const { messageId, requestMs, requestTries } = await this.opts.telegramClient.sendMessage({
                chatId,
                message,
                replyToMessageId,
                timeoutMs,
            });

            if (pinned) {
                await unpinAllChatMessages;
                await this.opts.telegramClient.pinChatMessage({ chatId, messageId, timeoutMs });
            }

            const successEvent: AnalyticsEvent = {
                user_id: receiverId,
                event_type: CHATBOT_MESSAGE_SUCCESS,
                event_properties: { ...commonEventProps, requestMs, requestTries },
                user_properties: analyticsUserProperties,
            };

            await Promise.all([
                this.opts.apiAccess.sendAnalyticsEvents([successEvent]),

                this.db.chatbot
                    .setLastReceivedMessagePayload(receiverId, {
                        timestamp: this.opts.apiAccess.dateNow(),
                        ...payload,
                    })
                    .catch((error) => this.opts.apiAccess.reportError(error)), // No need to surface these DB errors to the client
            ]);

            return { ok: true };
        } catch (error: any) {
            const failureEvent: AnalyticsEvent = {
                user_id: receiverId,
                event_type: CHATBOT_MESSAGE_FAILURE,
                event_properties: { error: error.message, ...commonEventProps },
                user_properties: analyticsUserProperties,
            };

            // Save Sentry quota by not reporting the common `user blocked bot` and rate limit errors:
            const skipErrorReport = error instanceof ResponseError && [403, 429].includes(error.statusCode);

            const errorReport = skipErrorReport
                ? undefined
                : new ReplicantError(
                      `Telegram chatbot message send failed: ${error.message}`,
                      'server_error',
                      'chatbot_send_error',
                      'error',
                      { receiverId, message, templateName, reason: error },
                  );

            await Promise.all([
                this.opts.apiAccess.sendAnalyticsEvents([failureEvent]),

                errorReport ? this.opts.apiAccess.reportError(errorReport) : undefined,
            ]);

            return { ok: false, error };
        }
    };

    async sendDiscordChatbotMessage(opts: { receiverId: string; senderId: string; message: ChatbotMessageInstance }) {
        const { discordClient } = this.opts;

        if (!discordClient) {
            throw Error('Discord client not configured. Make sure to set the DISCORD_BOT_TOKEN environment variable.');
        }

        await this.sendMessage(
            (renderedMessage) => discordClient.sendMessage({ userId: opts.receiverId, message: renderedMessage }),
            {
                message: opts.message,
                receiverId: opts.receiverId,
                rendererType: 'discord',
                senderId: opts.senderId,
                skipErrorReportIf: (error) => error instanceof ResponseError && [403, 429].includes(error.statusCode), // Save Sentry quota by not reporting the common `user blocked bot` and rate limit errors
            },
        );
    }

    async sendFacebookA2UChatbotMessage(opts: {
        delayMs?: number;
        receiverId: string;
        senderId: string;
        message: ChatbotMessageInstance;
    }) {
        const { fbGamingGraphAPIClient } = this.opts;

        if (!fbGamingGraphAPIClient) {
            throw Error(
                'FB Gaming Graph API client is not configured. Use the Facebook platform and configure app ID and secret.',
            );
        }

        const { delayMs, message, receiverId, senderId } = opts;

        const { appScopedUserId } = (await this.db.chatbot.fetchMetainfo(receiverId)) ?? {};

        if (!appScopedUserId) {
            return;
        }

        await this.sendMessage(
            async (renderedMessage) => {
                await fbGamingGraphAPIClient.a2u.sendNotification(appScopedUserId, {
                    ...renderedMessage,
                    payload: message.payload,
                    delayMs,
                });
            },
            {
                message,
                receiverId,
                rendererType: 'facebookA2U',
                senderId,
            },
        );
    }

    private async sendWebPushNotification(opts: {
        receiverChatbotMetainfo: ChatbotMetainfo;
        receiverId: string;
        senderId: string;
        message: ChatbotMessageInstance;
        payload: AnalyticsPayload;
        sessionId: string;
    }) {
        const pushSubscription = opts.receiverChatbotMetainfo.webPushSubscription;
        if (!pushSubscription) {
            return { ok: false, reason: 'Web Push not subscribed' };
        }
        const renderer = opts.message.renderers.webpush;

        if (!renderer) {
            return { ok: false, reason: 'Web Push renderer not found' };
        }

        const data = renderer({
            args: opts.message.args,
            payload: opts.payload,
            api: this.opts.apiAccess.createChatbotRendererApi({ receiverId: opts.receiverId, senderId: opts.senderId }),
        });

        try {
            await this.opts.webPushClient.sendNotification(pushSubscription, data);
        } catch (error) {
            const failureAnalyticsEvent: AnalyticsEvent = {
                user_id: opts.receiverId,
                event_type: WEBPUSH_NOTIFICATION_FAILURE,
                event_properties: {
                    template: opts.message.name,
                    error: error instanceof Error ? error.message : (error as string),
                    ...opts.payload,
                },
                insert_id: generateInsertId(opts.receiverId, opts.sessionId),
            };
            await this.opts.apiAccess.sendAnalyticsEvents([failureAnalyticsEvent]);

            if (error instanceof Error) {
                await this.opts.apiAccess.reportError(error, {
                    data,
                    receiverId: opts.receiverId,
                    senderId: opts.senderId,
                });
            }

            return { ok: false, reason: `${error as any}` };
        }

        const successAnalyticsEvent: AnalyticsEvent = {
            user_id: opts.receiverId,
            event_type: WEBPUSH_NOTIFICATION_SUCCESS,
            event_properties: {
                template: opts.message.name,
                ...opts.payload,
            },
            user_properties: opts.message.analyticsUserProperties,
            insert_id: generateInsertId(opts.receiverId, opts.sessionId),
        };
        await this.opts.apiAccess.sendAnalyticsEvents([successAnalyticsEvent]);

        return { ok: true };
    }

    private async sendSmsNotification(opts: {
        receiverChatbotMetainfo: ChatbotMetainfo;
        receiverId: string;
        senderId: string;
        message: ChatbotMessageInstance;
        payload: AnalyticsPayload;
        sessionId: string;
    }) {
        if (!opts.receiverChatbotMetainfo.smsSubscribed) {
            return { ok: false, reason: 'SMS not subscribed' };
        }
        const phoneNumber = await this.db.accountLink.getPhoneNumber(opts.receiverId);
        const renderer = opts.message.renderers.sms;

        if (!phoneNumber) {
            throw new Error('Receiver is subscribed to SMS but no valid phone number found');
        }

        if (!renderer) {
            return { ok: false, reason: 'SMS renderer not found' };
        }

        await this.opts.twilioClient.sendSms({
            receiver: phoneNumber,
            body: renderer({
                args: opts.message.args,
                payload: opts.payload,
                api: this.opts.apiAccess.createChatbotRendererApi({
                    receiverId: opts.receiverId,
                    senderId: opts.senderId,
                }),
            }),
        });
        return { ok: true };
    }

    private async sendActivePushNotification(opts: {
        receiverChatbotMetainfo: ChatbotMetainfo;
        receiverId: string;
        senderId: string;
        message: ChatbotMessageInstance;
        payload: AnalyticsPayload;
        sessionId: string;
    }): Promise<{ ok: true } | { ok: false; reason: string }> {
        const renderer = opts.message.renderers.ios;
        if (!renderer) {
            return { ok: false, reason: 'iOS renderer not found' };
        }
        const { receiverChatbotMetainfo, receiverId, senderId, message, payload, sessionId } = opts;

        const appleDeviceToken = receiverChatbotMetainfo.appleDeviceToken!;
        const rawPayload = renderer({
            args: message.args,
            payload,
            api: this.opts.apiAccess.createChatbotRendererApi({ receiverId, senderId }),
        });

        const response = await this.opts.sendIOSPushNotification({
            receiverId,
            rawPayload,
            userSessionId: sessionId,
            appleDeviceToken,
            analyticsEventProperties: {
                ...payload,
                template: message.name,
            },
            analyticsUserProperties: message.analyticsUserProperties,
        });

        return { ok: response.success, reason: response.reason || 'Sending iOS push notification failed' };
    }

    private async sendAndroidPushNotification(opts: {
        receiverChatbotMetainfo: ChatbotMetainfo;
        receiverId: string;
        senderId: string;
        message: ChatbotMessageInstance;
        payload: AnalyticsPayload;
        sessionId: string;
    }): Promise<{ ok: true } | { ok: false; reason: string }> {
        const renderer = opts.message.renderers.android;
        if (!renderer) {
            return { ok: false, reason: 'Android renderer not found' };
        }

        const { receiverChatbotMetainfo, receiverId, senderId, message, payload: origPayload, sessionId } = opts;

        const androidDeviceToken = receiverChatbotMetainfo.androidDeviceToken!;

        const { notification, payload } = renderer({
            args: message.args,
            payload: origPayload,
            api: this.opts.apiAccess.createChatbotRendererApi({ receiverId, senderId }),
        });

        const response = await this.opts.sendAndroidPushNotification({
            receiverId,
            androidDeviceToken,
            notification,
            payload,
            userSessionId: sessionId,
            analyticsEventProperties: {
                ...origPayload,
                template: message.name,
            },
            analyticsUserProperties: message.analyticsUserProperties,
        });

        return { ok: response.success, reason: response.reason || 'Sending Android push notification failed' };
    }

    private async sendPlatformChatbotMessage(opts: {
        receiverChatbotMetainfo: ChatbotMetainfo;
        receiverId: string;
        senderId: string;
        message: ChatbotMessageInstance;
        payload: AnalyticsPayload & { timestamp: number };
        sessionId: string;
    }): Promise<{ ok: true } | { ok: false; reason: string }> {
        let postChatbotMessage: PostMessageToChatbotFn;
        let rendererPlatform: 'facebook' | 'line' | 'zoom';

        switch (this.opts.apiAccess.platform) {
            case 'fb': {
                postChatbotMessage = this.opts.postFBMessage;
                rendererPlatform = 'facebook';
                break;
            }
            case 'line': {
                postChatbotMessage = this.opts.postLineMessage;
                rendererPlatform = 'line';
                break;
            }
            case 'zoom': {
                postChatbotMessage = this.opts.postZoomMessage;
                rendererPlatform = 'zoom';
                break;
            }
            default: {
                return {
                    ok: false,
                    reason: `Platform ${this.opts.apiAccess.platform} does not support a platform chatbot`,
                };
            }
        }

        const renderer = opts.message.renderers[rendererPlatform];
        if (!renderer) {
            return { ok: false, reason: `Renderer for platform ${rendererPlatform} not found` };
        }
        const { receiverChatbotMetainfo, senderId, receiverId, message, payload: origPayload, sessionId } = opts;

        // No messenger id set, only iOS token, bail from here on out.
        if (!receiverChatbotMetainfo.messengerId) {
            return { ok: false, reason: 'No messenger ID set' };
        }

        if (!receiverChatbotMetainfo.subscribed) {
            return { ok: false, reason: 'User is not subscribed to chatbot' };
        }

        const now = this.opts.apiAccess.dateNow();

        if (platformEnforcesChatbotQuotas(this.opts.apiAccess.platform)) {
            // Check if we have the quota to send the message.
            const messagesSinceLastSession = receiverChatbotMetainfo.messagesSinceLastSession || 0;

            if (messagesSinceLastSession >= this.opts.apiAccess.config.chatbot!.opts.maxMessagesPerSession) {
                return { ok: false, reason: 'Message quota exceeded' };
            }

            const lastSessionDate = receiverChatbotMetainfo.timeLastSessionEnded;

            // Check if session is not too old.
            if (
                lastSessionDate !== undefined &&
                lastSessionDate <
                    now - duration({ days: this.opts.apiAccess.config.chatbot!.opts.maxDaysSinceLastSession })
            ) {
                return { ok: false, reason: 'Last session date is too old' };
            }
        }

        const delayedActions: DelayedAction[] = [];

        const encodedPayload = await this.opts.apiAccess.config.encodePlatformMessagePayload?.(
            origPayload,
            this.opts.createPayloadEncoderAPI({
                userId: senderId,
                receiverId,
                sessionId,
                delayedActions,
            }),
        );

        await this.opts.apiAccess.resolveDelayedActions(delayedActions);

        const payload = renderer({
            args: message.args,
            payload: encodedPayload ?? origPayload,
            api: this.opts.apiAccess.createChatbotRendererApi({ receiverId, senderId }),
        });

        const msgResponse = await postChatbotMessage(receiverChatbotMetainfo.messengerId, payload).catch((error) => ({
            error,
        }));

        if ('error' in msgResponse || msgResponse.statusCode < 200 || msgResponse.statusCode >= 300) {
            let parsedResponseBody;

            if ('body' in msgResponse) {
                try {
                    parsedResponseBody = JSON.parse(msgResponse.body);
                } catch {
                    // swallow JSON parse error
                }
            }

            const failureAnalyticsEvent: AnalyticsEvent = {
                user_id: receiverId,
                event_type: CHATBOT_MESSAGE_FAILURE,
                event_properties: {
                    requestMs: 'requestMs' in msgResponse ? msgResponse.requestMs : undefined,
                    requestTries: 'requestTries' in msgResponse ? msgResponse.requestTries : undefined,
                    template: message.name,
                    ...origPayload,
                    ...('error' in msgResponse
                        ? { error: msgResponse.error.message || msgResponse.error }
                        : {
                              response: {
                                  body: parsedResponseBody || msgResponse.body,
                                  status_code: msgResponse.statusCode,
                              },
                          }),
                },
                insert_id: generateInsertId(receiverId, sessionId),
            };

            if (this.opts.apiAccess.platform === 'fb') {
                failureAnalyticsEvent.event_properties.messagesSinceLastSession =
                    receiverChatbotMetainfo.messagesSinceLastSession || 0;
            }

            await Promise.all([
                this.opts.apiAccess.sendAnalyticsEvents([failureAnalyticsEvent]),
                this.reportPlatformAPIError({
                    analyticsPayload: payload,
                    error: 'error' in msgResponse ? msgResponse.error : parsedResponseBody || msgResponse.body,
                    receiverChatbotMetainfo,
                    receiverId,
                    templateName: message.name,
                }),
            ]);

            return {
                ok: false,
                reason:
                    'error' in msgResponse
                        ? msgResponse.error.message || msgResponse.error
                        : msgResponse.body || msgResponse.statusCode + '',
            };
        }

        const successAnalyticsEvent: AnalyticsEvent = {
            user_id: receiverId,
            event_type: CHATBOT_MESSAGE_SUCCESS,
            event_properties: {
                requestMs: msgResponse.requestMs,
                requestTries: msgResponse.requestTries,
                template: message.name,
                ...origPayload,
            },
            user_properties: message.analyticsUserProperties,
            insert_id: generateInsertId(receiverId, sessionId),
        };

        if (this.opts.apiAccess.platform === 'fb') {
            // +1 analytics value since it was sent but gets incremented by markMessageSent below
            successAnalyticsEvent.event_properties.messagesSinceLastSession =
                (receiverChatbotMetainfo.messagesSinceLastSession || 0) + 1;
        }

        await Promise.all([
            this.opts.apiAccess.sendAnalyticsEvents([successAnalyticsEvent]),

            this.db.chatbot
                .setLastReceivedMessagePayload(receiverId, origPayload)
                .then(async () => {
                    // markMessageSent sets metainfo properties, required to figure out
                    // if we exceeded facebook quotas on messaging. We can save db-writes
                    // by omitting calling it for other platforms
                    if (this.opts.apiAccess.platform === 'fb') {
                        await this.db.chatbot.markMessageSent(receiverId, now);
                    }
                })
                .catch((error) => this.opts.apiAccess.reportError(error)), // No need to surface these DB errors to the client
        ]);

        return { ok: true };
    }

    private async sendLinePlatformChatbotMessage(
        opts: SendLineChatbotMessageOptions & {
            receiverChatbotMetainfo: ChatbotMetainfo;
            sessionId: string;
            payload: AnalyticsPayload & { timestamp: number };
        },
    ): Promise<{ ok: true } | { ok: false; reason: string }> {
        if (this.opts.apiAccess.platform !== 'line') {
            throw new ReplicantError(
                'Chatbot message batching is not supported on platform ' + this.opts.apiAccess.platform,
                'server_error',
                'invalid_platform',
                'fatal',
            );
        }

        const { receiverChatbotMetainfo, receiverId, senderId, sessionId, message, payload, type } = opts;

        if (!message.renderers.line) {
            return { ok: false, reason: `Renderer for platform ${this.opts.apiAccess.platform} not found` };
        }

        // No messenger id set, only iOS token, bail from here on out.
        if (!receiverChatbotMetainfo.messengerId) {
            return { ok: false, reason: 'No messenger ID set' };
        }

        if (!receiverChatbotMetainfo.subscribed) {
            return { ok: false, reason: 'User is not subscribed to chatbot' };
        }

        const delayedActions: DelayedAction[] = [];
        const payloadEncoderAPI = this.opts.createPayloadEncoderAPI({
            userId: senderId,
            receiverId,
            sessionId,
            delayedActions,
        });

        const encodedPayload = await this.opts.apiAccess.config.encodePlatformMessagePayload?.(
            payload,
            payloadEncoderAPI,
        );

        await this.opts.apiAccess.resolveDelayedActions(delayedActions);

        const messagePayload = message.renderers.line({
            args: message.args,
            payload: encodedPayload ?? payload,
            api: this.opts.apiAccess.createChatbotRendererApi({ receiverId, senderId }),
        });

        const sendMessagePromise =
            type === 'push'
                ? opts.sendMessageToLine({
                      type: 'push',
                      userId: opts.receiverId,
                      message: messagePayload,
                  })
                : type === 'reply'
                ? opts.sendMessageToLine({
                      type: 'reply',
                      replyToken: opts.replyToken,
                      message: messagePayload,
                  })
                : unreachable(type);

        const msgResponse = await sendMessagePromise.catch((error) => ({ error }));

        let analyticsUserProperties: { [key: string]: unknown } | undefined;

        if ('error' in msgResponse || msgResponse.statusCode < 200 || msgResponse.statusCode >= 300) {
            let parsedResponseBody;

            if ('body' in msgResponse) {
                try {
                    parsedResponseBody = JSON.parse(msgResponse.body);
                } catch {
                    // swallow JSON parse error
                }
            }

            const failureAnalyticsEvent: AnalyticsEvent = {
                user_id: receiverId,
                ...(type === 'push'
                    ? {
                          event_type: CHATBOT_MESSAGE_FAILURE,
                      }
                    : {
                          event_type: CHATBOT_RESPONSE_FAILURE,
                      }),
                event_properties: {
                    requestMs: 'requestMs' in msgResponse ? msgResponse.requestMs : undefined,
                    requestTries: 'requestTries' in msgResponse ? msgResponse.requestTries : undefined,
                    template: message.name,
                    ...payload,
                    ...('error' in msgResponse
                        ? { error: msgResponse.error.message || msgResponse.error }
                        : {
                              response: {
                                  body: parsedResponseBody || msgResponse.body,
                                  status_code: msgResponse.statusCode,
                              },
                          }),
                },
                insert_id: generateInsertId(receiverId, sessionId),
            };

            await Promise.all([
                this.opts.apiAccess.sendAnalyticsEvents([failureAnalyticsEvent]),
                this.reportPlatformAPIError({
                    analyticsPayload: payload,
                    error: 'error' in msgResponse ? msgResponse.error : parsedResponseBody || msgResponse.body,
                    receiverChatbotMetainfo,
                    receiverId,
                    templateName: message.name,
                }),
            ]);

            return {
                ok: false,
                reason:
                    'error' in msgResponse
                        ? msgResponse.error.message || msgResponse.error
                        : msgResponse.body || msgResponse.statusCode + '',
            };
        }

        const successAnalyticsEvent: AnalyticsEvent = {
            user_id: receiverId,
            ...(type === 'push'
                ? {
                      event_type: CHATBOT_MESSAGE_SUCCESS,
                  }
                : {
                      event_type: CHATBOT_RESPONSE_SUCCESS,
                  }),
            event_properties: {
                requestMs: msgResponse.requestMs,
                requestTries: msgResponse.requestTries,
                template: message.name,
                ...payload,
            },
            user_properties: analyticsUserProperties,
            insert_id: generateInsertId(receiverId, sessionId),
        };

        await Promise.all([
            this.opts.apiAccess.sendAnalyticsEvents([successAnalyticsEvent]),

            this.db.chatbot
                .setLastReceivedMessagePayload(receiverId, payload)
                .catch((error) => this.opts.apiAccess.reportError(error)), // No need to surface these DB errors to the client
        ]);

        return { ok: true };
    }

    // TODO: refactor all public send methods to use this helper:
    private async sendMessage<T extends RendererType>(
        sendFn: (renderedMessage: RendererResult<T>) => Promise<void>,
        opts: {
            chatbotType?: 'messenger';
            extraEventProperties?: Record<string, unknown>;
            message: ChatbotMessageInstance;
            receiverId: string;
            rendererType: T;
            senderId: string;
            skipErrorReportIf?: (error: unknown) => boolean;
        },
    ): Promise<void> {
        const { message, receiverId, rendererType, senderId, skipErrorReportIf } = opts;

        const renderer = message.renderers[rendererType];
        if (!renderer) {
            throw Error(`No ${rendererType} renderer in message template ${message.name}`);
        }

        const renderedMessage = renderer({
            args: message.args,
            payload: { timestamp: this.opts.apiAccess.dateNow(), ...message.payload },
            api: this.opts.apiAccess.createChatbotRendererApi({ receiverId, senderId }),
        });

        const chatbotType = opts.chatbotType ?? opts.rendererType;

        const eventProperties = {
            platform: chatbotType,
            template: message.name,
            ...message.payload,
        };

        try {
            await sendFn(renderedMessage as RendererResult<T>);

            const successEvent: AnalyticsEvent = {
                user_id: opts.receiverId,
                event_type: CHATBOT_MESSAGE_SUCCESS,
                event_properties: eventProperties,
                user_properties: message.analyticsUserProperties,
            };

            await Promise.all([
                this.opts.apiAccess.sendAnalyticsEvents([successEvent]),

                await this.db.chatbot
                    .setLastReceivedMessagePayload(receiverId, {
                        timestamp: this.opts.apiAccess.dateNow(),
                        ...message.payload,
                    })
                    .catch((error) => this.opts.apiAccess.reportError(error)), // No need to surface these DB errors to the client
            ]);
        } catch (error: any) {
            const failureEvent: AnalyticsEvent = {
                user_id: opts.receiverId,
                event_type: CHATBOT_MESSAGE_FAILURE,
                event_properties: { ...eventProperties, error: error.message },
                user_properties: message.analyticsUserProperties,
            };

            if (error instanceof ResponseError) {
                try {
                    failureEvent.event_properties.response = {
                        body: JSON.parse(error.responseBody),
                        status_code: error.statusCode,
                    };
                } catch {}
            }

            const errorReport =
                skipErrorReportIf && skipErrorReportIf(error)
                    ? undefined
                    : new ReplicantError(
                          `${chatbotType} chatbot message send failed: ${error.message}`,
                          'server_error',
                          'chatbot_send_error',
                          'error',
                          {
                              receiverId,
                              templateArgs: message.args,
                              templateName: message.name,
                              reason: error,
                          },
                      );

            await Promise.all([
                this.opts.apiAccess.sendAnalyticsEvents([failureEvent]),

                errorReport ? this.opts.apiAccess.reportError(errorReport) : undefined,
            ]);
        }
    }

    private async reportPlatformAPIError(opts: {
        analyticsPayload: { [key: string]: any } | string;
        error: { [key: string]: any } | Error | string | undefined;
        receiverChatbotMetainfo: ChatbotMetainfo;
        receiverId: string;
        templateName: string;
    }): Promise<void> {
        const { analyticsPayload, receiverChatbotMetainfo, receiverId, templateName } = opts;

        // Compute some helpful metrics.
        const lastSessionDate = receiverChatbotMetainfo.timeLastSessionEnded;
        const secondsSinceLastSession = lastSessionDate
            ? Math.floor((this.opts.apiAccess.dateNow() - lastSessionDate) / 1000)
            : 0;

        let error = opts.error;

        if (this.opts.apiAccess.platform === 'fb') {
            const fbError = typeof error === 'object' && 'error' in error ? error.error : undefined;

            if (fbError) {
                error = fbError;

                const code = Number(fbError.code);
                const subcode = Number(fbError.error_subcode);

                // Skip unknown errors
                if (
                    fbError.code === undefined || // Unknown internal error
                    code === 1 || // Unknown internal error
                    code === -1 // Unknown internal error
                ) {
                    return;
                }

                // Skip noisy errors and only report actionable items like Page Access Token errors
                if (
                    !process.env.LOG_VERBOSE_CHATBOT_ERRORS &&
                    ((code === 10 && subcode === 2018108) || // This Person Cannot Receive Messages: This person isn't receiving messages from you right now.
                        (code === 10 && subcode === 2018278) || // This message is being sent outside the allowed window.
                        (code === 100 && subcode === 2018144) || // Messages to an Instant Games user have exceeded Instant Games policy.
                        (code === 200 && subcode === 1545041) || // Message Not Sent: This person isn't available right now.
                        (code === 551 && subcode === 1545041) || // This person isn't available right now.
                        (code === 613 && subcode === 2018144)) // "Calls to this api have exceeded the rate limit" or "Could not send Instant Game message to the user at this time".
                ) {
                    return;
                }
            }
        }

        const errorMessage = typeof error === 'string' ? error : error?.message;

        await this.opts.apiAccess.reportError(
            new ReplicantError(
                'Platform chatbot message send failed: ' + (errorMessage || 'unknown platform error'),
                'server_error',
                'chatbot_send_error',
                'error',
                {
                    analyticsPayload: JSON.stringify(analyticsPayload),
                    secondsSinceLastSession,
                    receiverId,
                    receiverChatbotMetainfo,
                    templateName,
                    ...(typeof error === 'object' ? error : undefined),
                },
            ),
        );
    }
}

/** Currently only the Facebook platform enforces chatbot quotas: https://developers.facebook.com/docs/games/instant-games/guides/bots-and-server-communication#do-not-. */
export function platformEnforcesChatbotQuotas(platform: ReplicantPlatform): boolean {
    return platform === 'fb';
}
